import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { PAGE_SIZE } from '../api/reportingApiService'
import { PaginationSelectOption } from '../composites/GamesOverview/components/PaginationControls'
import { noop } from '../util/functional'
import { BadgeInfo, GameReportSummaryData, PlayerDataRow, TaskDataRow } from './ReportsContextHelper'
import { useGame } from './GameContext'
import { AnswerStateEnum, ObjectAny } from '../types/commonTypes'
import { getPlayerStateOverview } from '../pages/GameEditor/components/EditorSidebar/Play/helpers'
import { getTitleForTask } from '../pages/GameEditor/components/Tasks/TaskModal/helpers'
import { useTranslation } from 'react-i18next'
import { getCompletedAndEvaluatedCounts, getLastActionTime, getSumCompletionTime } from './GameReportContextHelper'
import { ExerciseType } from '../api/gameTypes'

export const PAGE_SIZE_OPTIONS = [
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 50, label: '50' },
]

export type PlayerFilters = {
  completion?: 'all' | 'completed' | 'uncompleted'
  badgeId?: number
}

export type PlayerSortOption = keyof Omit<PlayerDataRow, 'awardedBadges'>

export type TaskSortOption = keyof Omit<TaskDataRow, 'type'>

export enum ReportSortDir {
  ASC = ' asc',
  DESC = ' desc',
}

export interface GameReportDataProviderInterface {
  summaryData?: GameReportSummaryData
  currentPage: number
  pageSize: number
  pageData: PlayerDataRow[] | undefined
  tasksData: TaskDataRow[] | undefined
  loading: boolean
  lastPage: number
  allPageOptions: PaginationSelectOption[]
  selectPageSize: (newPageSize: number) => void
  prev: () => void
  next: () => void
  setPage: (newPage: number) => void
  playerSortBy?: PlayerSortOption
  taskSortBy?: TaskSortOption
  playerSortDir?: ReportSortDir
  taskSortDir?: ReportSortDir
  setPlayerSort?: (sortBy: PlayerSortOption, sortDir: ReportSortDir) => void
  setTaskSort?: (sortBy: TaskSortOption, sortDir: ReportSortDir) => void
  playerFilters: PlayerFilters
  setPlayerFilters: (filters: PlayerFilters) => void
  fetchSummaryData?: (gameId: number) => void
  fetchPlayersData?: (gameId: number, fromDate?: string, untilDate?: string) => Promise<PlayerDataRow[]>
  getAllPlayerDataRows?: () => PlayerDataRow[]
  playerCount: number
}

const GameReportDataContext = createContext<GameReportDataProviderInterface>({
  summaryData: undefined,
  currentPage: 1,
  pageSize: PAGE_SIZE,
  pageData: undefined,
  tasksData: undefined,
  loading: true,
  lastPage: 1,
  allPageOptions: [] as PaginationSelectOption[],
  selectPageSize: noop,
  prev: noop,
  next: noop,
  setPage: noop,
  playerSortBy: 'rank',
  playerSortDir: ReportSortDir.ASC,
  taskSortBy: 'name',
  taskSortDir: ReportSortDir.ASC,
  setPlayerSort: noop,
  setTaskSort: noop,
  playerFilters: {},
  setPlayerFilters: (filters: PlayerFilters) => {},
  playerCount: 0,
})

interface Props {
  gameId: number
  children?: React.ReactNode
}

const paginate = (
  arrayData: ObjectAny,
  pageSize: number,
  currentPage: number,
  sortBy: string,
  sortDir: ReportSortDir,
): PlayerDataRow[] | TaskDataRow[] => {
  return arrayData
    .sort((a: any, b: any) => {
      const aValue: number | string = typeof a[sortBy] !== 'string' && isNaN(a[sortBy]) ? '0' : a[sortBy] ?? 0
      const bValue: number | string = typeof a[sortBy] !== 'string' && isNaN(b[sortBy]) ? '0' : b[sortBy] ?? 0
      return sortDir === ReportSortDir.ASC ? (aValue > bValue ? 1 : -1) : aValue < bValue ? 1 : -1
    })
    .slice(pageSize * (currentPage - 1), pageSize * currentPage)
}

export const GameReportDataProvider: React.FC<Props> = ({ gameId, children }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE)
  const [loading] = useState(false)
  const [allPageOptions, setAllPageOptions] = useState<PaginationSelectOption[]>([{ value: 1, label: '1' }])
  const [playerSortBy, setPlayerSortBy] = useState<PlayerSortOption>('rank')
  const [playerSortDir, setPlayerSortDir] = useState<ReportSortDir>(ReportSortDir.ASC)
  const [taskSortBy, setTaskSortBy] = useState<TaskSortOption>('name')
  const [taskSortDir, setTaskSortDir] = useState<ReportSortDir>(ReportSortDir.ASC)
  const [playerFilters, setPlayerFilters] = useState<PlayerFilters>({})
  const { gameData, people, tasks } = useGame()

  const [playerCount, setPlayerCount] = useState(people.players.filter((p) => !p.deleted).length)
  const [lastPage, setLastPage] = useState(Math.ceil(people.players.filter((p) => !p.deleted).length / pageSize))
  const { t } = useTranslation()

  const tasksData = useMemo((): TaskDataRow[] | undefined => {
    if (!gameData || !tasks) {
      return undefined
    }
    const deletedPlayerIds = people.players.filter((p) => p.deleted).map((p) => p.id)
    const temp: TaskDataRow[] = tasks.map((task) => {
      const answerCount = task.subtasks[0]?.answers?.filter((a) => !deletedPlayerIds.includes(a.userId)).length ?? 0
      const { completedCount, evaluatedCount } = getCompletedAndEvaluatedCounts(people, task)
      let totalPointsFromTask = 0
      task.subtasks.forEach((t) => {
        t.answers?.forEach((a) => {
          totalPointsFromTask += a.points
        })
      })
      const sumCompletionTime = getSumCompletionTime(task)
      const averagePoints = Math.ceil(totalPointsFromTask / answerCount)
      const maxPoints = task.subtasks
        .map((t) => (t.type === ExerciseType.PollExercise && t.data.hasTotalPoints ? t.data.totalPoints : t.maxPoints))
        .reduce((a, c) => {
          return a + c
        }, 0)
      const averageScorePercentage = Math.ceil((100 * averagePoints) / maxPoints)
      const averageTime = Math.ceil(sumCompletionTime / 1000 / answerCount)
      return {
        task,
        id: task.id,
        name: task.name,
        type: getTitleForTask(task, t),
        rawType: task.type,
        answerCount,
        maxPoints,
        completedCount,
        evaluatedCount,
        averagePoints,
        averageScorePercentage,
        averageTime,
        lastActionTime: getLastActionTime(task),
      }
    })
    return paginate(temp, 200, 1, taskSortBy, taskSortDir) as TaskDataRow[]
  }, [gameData, people, t, taskSortBy, taskSortDir, tasks])

  const getAllPlayerDataRows = useCallback((): PlayerDataRow[] => {
    if (!gameData || !tasks || !people) {
      return []
    }
    const playerData = people.players
      .map((p) => {
        //Need to override the player's points here as they are not updated in poeple players realtime
        let totalPoints = 0
        tasks.forEach((task) => {
          task.subtasks.forEach((subtask) => {
            const playerAnswer = subtask.answers?.filter((a) => a.userId === p.id)
            totalPoints = totalPoints + (playerAnswer && playerAnswer[0] ? playerAnswer[0].points : 0)
          })
        })
        return {
          ...p,
          points: totalPoints,
        }
      })
      .filter((p) => !p.deleted)
      .sort((a, b) => b.points - a.points)
      .map((player, index) => {
        let totalSpentTime = 0
        let totalAnswersCount = 0
        tasks.forEach((task) => {
          const answerTime = task.subtasks[0].answers
            ?.filter((a) => a.userId === player.id && a.state !== AnswerStateEnum.REVISION)
            ?.map((a) => {
              return new Date(a.completedAt).getTime() - new Date(a.openedAt).getTime()
            })
          if (answerTime && answerTime.length > 0) {
            totalAnswersCount++
            totalSpentTime += answerTime[0] / 1000
          }
        })
        const status = getPlayerStateOverview(player.id, tasks, gameData)
        return {
          rank: index + 1,
          nickName: player.nickName,
          email: player.email,
          teamMemberNames: player.teamMemberNames,
          points: player.points,
          correctPercentage: Math.min((100 * player.points) / gameData.maxPoints, 100),
          gameDuration: totalSpentTime,
          completionRate: Math.ceil(Math.min((100 * totalAnswersCount) / tasks.length, 100)),
          awardedBadges: player.badges,
          overallCompleted: status.gameCompleted,
        }
      })
    return playerData
  }, [gameData, people, tasks])

  const pageData = useMemo((): PlayerDataRow[] | undefined => {
    if (!gameData || !tasks || !people) {
      return undefined
    }
    const playerData = people.players
      .map((p) => {
        //Need to override the player's points here as they are not updated in poeple players realtime
        let totalPoints = 0
        tasks.forEach((task) => {
          task.subtasks.forEach((subtask) => {
            const playerAnswer = subtask.answers?.filter((a) => a.userId === p.id)
            totalPoints = totalPoints + (playerAnswer && playerAnswer[0] ? playerAnswer[0].points : 0)
          })
        })
        return {
          ...p,
          points: totalPoints,
        }
      })
      .filter((p) => !p.deleted)
      .sort((a, b) => b.points - a.points)
      .map((player, index) => {
        let totalSpentTime = 0
        let totalAnswersCount = 0
        tasks.forEach((task) => {
          const answerTime = task.subtasks[0].answers
            ?.filter((a) => a.userId === player.id && a.state !== AnswerStateEnum.REVISION)
            ?.map((a) => {
              return new Date(a.completedAt).getTime() - new Date(a.openedAt).getTime()
            })
          if (answerTime && answerTime.length > 0) {
            totalAnswersCount++
            totalSpentTime += answerTime[0] / 1000
          }
        })
        const status = getPlayerStateOverview(player.id, tasks, gameData)
        return {
          rank: index + 1,
          nickName: player.nickName,
          email: player.email,
          teamMemberNames: player.teamMemberNames,
          points: player.points,
          correctPercentage: Math.min((100 * player.points) / gameData.maxPoints, 100),
          gameDuration: totalSpentTime,
          completionRate: Math.ceil(Math.min((100 * totalAnswersCount) / tasks.length, 100)),
          awardedBadges: player.badges,
          overallCompleted: status.gameCompleted,
        }
      })
      .filter((p) => !playerFilters.badgeId || p.awardedBadges.findIndex((b) => b.id === playerFilters.badgeId) > -1)
      .filter((p) => playerFilters.completion !== 'completed' || p.completionRate === 100 || p.overallCompleted)
      .filter((p) => playerFilters.completion !== 'uncompleted' || (p.completionRate < 100 && !p.overallCompleted))

    setPlayerCount(playerData.length)
    setLastPage(Math.ceil(playerData.length / pageSize))

    return paginate(playerData, pageSize, currentPage, playerSortBy, playerSortDir) as PlayerDataRow[]
  }, [
    gameData,
    tasks,
    people,
    pageSize,
    currentPage,
    playerSortBy,
    playerSortDir,
    playerFilters.badgeId,
    playerFilters.completion,
  ])

  const summaryData = useMemo((): GameReportSummaryData | undefined => {
    if (!gameData || !tasks) {
      return undefined
    }
    const playerCount = people.players.filter((p) => !p.deleted).length
    const playersNotFinished: string[] = []
    const playerStatuses = people.players
      .filter((p) => !p.deleted)
      .map((player) => {
        const temp = getPlayerStateOverview(player.id, tasks, gameData)
        if (!temp.gameCompleted) {
          playersNotFinished.push(player.nickName)
        }
        return temp
      })
    const playersCompletedCount = playerStatuses.filter((ps) => ps.gameCompleted).length
    const averageScore =
      people.players
        .filter((p) => !p.deleted)
        .map((p) => {
          //Need to override the player's points here as they are not updated in poeple players realtime
          let totalPoints = 0
          tasks.forEach((task) => {
            task.subtasks.forEach((subtask) => {
              const playerAnswer = subtask.answers?.filter((a) => a.userId === p.id)
              totalPoints = totalPoints + (playerAnswer && playerAnswer[0] ? playerAnswer[0].points : 0)
            })
          })
          return totalPoints
        })
        .reduce((acc, curr) => {
          return acc + curr
        }, 0) / playerCount
    const completionRate = (100 * playersCompletedCount) / playerCount
    let mostDifficultTask = { name: '', correctPercentage: 100, averageTime: 0 }
    let mostSuccesfulTask = { name: '', correctPercentage: 0, averageTime: 0 }
    const awardedBadges: BadgeInfo[] = []
    tasks
      .filter((t) => t.type !== ExerciseType.ExploreExercise)
      .forEach((task) => {
        task.subtasks[0].answers?.forEach((a) => {
          if (a.badge) {
            const ind = awardedBadges.findIndex((b) => b.id === a.badge?.id)
            if (ind > -1) {
              awardedBadges[ind].count++
            } else {
              awardedBadges.push({ ...a.badge, count: 1 })
            }
          }
        })
        let taskMaxTotalPoints = 0
        let allPlayersTotalPoints = 0
        task.subtasks.forEach((subtask) => {
          subtask.answers?.forEach((a) => {
            allPlayersTotalPoints += a.points
            taskMaxTotalPoints += subtask.maxPoints
          })
        })
        const sumCompletionTime = getSumCompletionTime(task)
        const completedPercentage = (100 * allPlayersTotalPoints) / taskMaxTotalPoints
        if (completedPercentage < mostDifficultTask.correctPercentage) {
          mostDifficultTask.name = task.name
          mostDifficultTask.correctPercentage = completedPercentage
          mostDifficultTask.averageTime = Math.ceil(sumCompletionTime / 1000 / (task.subtasks[0].answers?.length ?? 1))
        }
        if (completedPercentage > mostSuccesfulTask.correctPercentage) {
          mostSuccesfulTask.name = task.name
          mostSuccesfulTask.correctPercentage = completedPercentage
          mostSuccesfulTask.averageTime = Math.ceil(sumCompletionTime / 1000 / (task.subtasks[0].answers?.length ?? 1))
        }
      })

    return {
      gameName: gameData.name,
      completionRate,
      averageScore,
      playerCount,
      mostDifficultTask,
      mostSuccesfulTask,
      playersNotFinished,
      awardedBadges,
    }
  }, [gameData, people, tasks])

  const next = useCallback(() => {
    if (loading) {
      return
    }

    setCurrentPage((currentState) => currentState + 1)
  }, [loading])

  const prev = useCallback(() => {
    if (loading) {
      return
    }

    setCurrentPage((currentState) => currentState - 1)
  }, [loading])

  const selectPageSize = useCallback(
    (newPageSize: number) => {
      if (loading) {
        return
      }

      setCurrentPage(1)
      setPageSize(newPageSize)
    },
    [loading],
  )

  const setPage = useCallback(
    (newPage: number) => {
      if (loading) {
        return
      }

      setCurrentPage(newPage)
    },
    [loading],
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [playerFilters])

  useEffect(() => {
    const calculateAllPageOptions = Array.from({ length: lastPage }, (_, i) => ({
      value: i + 1,
      label: `${i + 1}`,
    }))
    setAllPageOptions(calculateAllPageOptions)
  }, [lastPage])

  const setPlayerSort = useCallback((sortBy: PlayerSortOption, sortDir: ReportSortDir) => {
    setPlayerSortBy(sortBy)
    setPlayerSortDir(sortDir)
    setCurrentPage(1)
  }, [])

  const setTaskSort = useCallback((sortBy: TaskSortOption, sortDir: ReportSortDir) => {
    setTaskSortBy(sortBy)
    setTaskSortDir(sortDir)
  }, [])

  return (
    <GameReportDataContext.Provider
      value={{
        summaryData,
        currentPage,
        pageSize,
        pageData,
        tasksData,
        lastPage,
        allPageOptions,
        loading,
        playerFilters,
        playerSortBy,
        playerSortDir,
        taskSortBy,
        taskSortDir,
        setPage,
        selectPageSize,
        next,
        prev,
        setPlayerFilters,
        setPlayerSort,
        setTaskSort,
        playerCount,
        getAllPlayerDataRows,
      }}
    >
      {children}
    </GameReportDataContext.Provider>
  )
}

export const useReportData = () => {
  const context = useContext(GameReportDataContext)

  return context
}
